"use client"

import type React from "react"

import { Link } from "react-router-dom"
import { Facebook, Twitter, Instagram, Linkedin, Github, Mail, MapPin, ArrowRight } from "lucide-react"
import { Separator } from "@/components/ui/separator"
import { useState } from "react"
import { subscribeToNewsletter } from "@/services/newsletterService"
import { toast } from "sonner"

export const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()

    // Basic email validation
    if (!email || !email.includes("@") || !email.includes(".")) {
      toast.error("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)

    try {
      await subscribeToNewsletter(email)
      setEmail("") // Clear form on success
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <footer className="w-full bg-gradient-to-br from-slate-950 to-slate-900 text-gray-300 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Newsletter Subscription */}
        <div className="mb-10 p-6 bg-slate-800/50 rounded-lg border border-slate-700/50">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="md:w-2/3">
              <h3 className="text-white text-lg font-bold mb-2">Stay Updated</h3>
              <p className="text-gray-400 text-sm">
                Subscribe to our newsletter for the latest trading insights, market analysis, and platform updates.
              </p>
            </div>
            <div className="md:w-1/3 w-full">
              <form className="flex flex-col sm:flex-row w-full" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-grow px-4 py-2 bg-slate-900 border border-slate-700 rounded-md sm:rounded-r-none sm:rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                <button
                  type="submit"
                  className={`mt-2 sm:mt-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md sm:rounded-l-none sm:rounded-r-md transition-colors duration-300 w-full sm:w-auto ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Subscribing..." : "Subscribe"}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Company Info - Wider column */}
          <div className="md:col-span-4 space-y-5">
            <div className="flex items-center space-x-3">
              <img
                src="/lovable-uploads/1091d23b-2cf4-40d4-b2ec-3d052c262c74.png"
                alt="Stewie Trader"
                className="h-10 w-auto"
              />
              <span className="text-white text-xl font-bold">Stewie Trader</span>
            </div>
            <p className="text-gray-400 leading-relaxed">
              Advanced AI-powered trading solutions that deliver institutional-grade analysis for investors of all
              levels.
            </p>
            <div className="flex space-x-4 pt-2">
              <a
                href="#"
                className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-sky-400 transition-colors duration-300"
                aria-label="Twitter"
              >
                <Twitter size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-pink-400 transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-blue-500 transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-gray-200 transition-colors duration-300"
                aria-label="GitHub"
              >
                <Github size={20} />
              </a>
            </div>
          </div>

          {/* Spacing column */}
          <div className="hidden md:block md:col-span-1"></div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h3 className="text-white font-semibold text-lg mb-4 border-b border-blue-700/30 pb-2">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Home</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/"
                  state={{ showEarlyAccess: true }}
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Sign Up</span>
                </Link>
              </li>
              <li>
                <a
                  href="https://v0-stewie-trader-executive-summary.vercel.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Investment Deck</span>
                </a>
              </li>
              <li>
                <Link
                  to="/studies"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Pattern Studies</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div className="md:col-span-2">
            <h3 className="text-white font-semibold text-lg mb-4 border-b border-blue-700/30 pb-2">Products</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Stewie Chat</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Stewie Trader</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Portfolio Analysis</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight
                    size={16}
                    className="mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                  />
                  <span>Risk Management</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h3 className="text-white font-semibold text-lg mb-4 border-b border-blue-700/30 pb-2">Contact Us</h3>
            <ul className="space-y-3">
              <li className="text-gray-400 flex items-start">
                <MapPin size={18} className="mr-3 mt-1 text-blue-400 flex-shrink-0" />
                <span>
                  102 W. Main St #907
                  <br />
                  New Albany, OH 43082
                </span>
              </li>
              <li className="text-gray-400 flex items-center">
                <Mail size={18} className="mr-3 text-blue-400 flex-shrink-0" />
                <a
                  href="mailto:support@stewietrader.com"
                  className="hover:text-blue-400 transition-colors duration-300"
                >
                  support@stewietrader.com
                </a>
              </li>
              <li className="text-gray-400 flex items-center pt-3">
                <a
                  href="#"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors duration-300 inline-flex items-center space-x-2 shadow-lg shadow-blue-900/20"
                >
                  <span>Contact Support</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Awards & Recognition Section */}
        <div className="my-10 py-6 border-y border-slate-800">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-12">
            <div className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-full mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">Trusted by</p>
                <p className="text-gray-400 text-sm">professional traders</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-full mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">Secure Platform</p>
                <p className="text-gray-400 text-sm">Enterprise-grade</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-full mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">Fast Analysis</p>
                <p className="text-gray-400 text-sm">Real-time results</p>
              </div>
            </div>
          </div>
        </div>

        <Separator className="mb-8 bg-gray-800/60" />

        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-sm text-gray-500">&copy; {currentYear} Stewie Trader. All rights reserved.</p>
          <div className="flex space-x-6 text-sm">
            <Link to="/terms" className="text-gray-500 hover:text-blue-400 transition-colors duration-300">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-gray-500 hover:text-blue-400 transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/cookies" className="text-gray-500 hover:text-blue-400 transition-colors duration-300">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
